import { datadogRum } from '@datadog/browser-rum'

/**
 * Datadog RUM credentials, baked into the bundle at build time. Both are
 * public client-side values (like the LaunchDarkly client ID). Empty locally
 * or before the RUM application is provisioned — RUM is then a no-op.
 */
const APPLICATION_ID: string =
    import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID ?? ''
const CLIENT_TOKEN: string = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN ?? ''

/** Deployment environment tag (test | stage | prod), matching server-side DD_ENV. */
const RUM_ENV: string = import.meta.env.VITE_DATADOG_RUM_ENV ?? ''

/** Release tag, shared with Sentry (latest git tag, set by build/build.sh). */
const RUM_VERSION: string = import.meta.env.VITE_SENTRY_RELEASE ?? ''

export const isRumEnabled: boolean =
    APPLICATION_ID !== '' && CLIENT_TOKEN !== ''

/**
 * Initialize Datadog RUM. No-op unless both `VITE_DATADOG_RUM_APPLICATION_ID`
 * and `VITE_DATADOG_RUM_CLIENT_TOKEN` are set, so local dev sends nothing.
 *
 * The app runs inside the BigCommerce control panel iframe (cross-site), so
 * the session cookie must be partitioned (CHIPS) to survive third-party
 * cookie blocking.
 */
export function initDatadogRum(): void {
    if (!isRumEnabled) {
        return
    }

    datadogRum.init({
        applicationId: APPLICATION_ID,
        clientToken: CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'appex-platform',
        env: RUM_ENV || undefined,
        version: RUM_VERSION || undefined,
        // Low traffic today: sample everything. Tune down as volume/cost grows.
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        // Never record user input; the app handles Algolia/BigCommerce credentials.
        defaultPrivacyLevel: 'mask-user-input',
        usePartitionedCrossSiteSessionCookie: true,
        // API calls are same-origin (/api/v1/...); dd-trace runs in the api so
        // trace headers give RUM -> APM correlation.
        allowedTracingUrls: [window.location.origin],
        // Attach feature flag evaluations to actions/vitals in addition to the
        // default views/errors, so redesign interactions are segmentable.
        trackFeatureFlagsForEvents: ['action', 'vital'],
    })
}

/**
 * Set a property on the RUM global context (attached to all subsequent
 * events). No-op when RUM is disabled. Never pass PII.
 *
 * @param key - Context property name.
 * @param value - Context property value.
 */
export function setRumGlobalContextProperty(key: string, value: unknown): void {
    if (!isRumEnabled) {
        return
    }
    datadogRum.setGlobalContextProperty(key, value)
}

/**
 * Report a feature flag evaluation to RUM (shows up in the Feature Flags
 * panel and is scoped to the current view). No-op when RUM is disabled.
 *
 * @param flagKey - The flag key as defined in LaunchDarkly (e.g. `appex-redesign`).
 * @param value - The evaluated flag value.
 */
export function addRumFeatureFlagEvaluation(
    flagKey: string,
    value: unknown
): void {
    if (!isRumEnabled) {
        return
    }
    datadogRum.addFeatureFlagEvaluation(flagKey, value)
}
